import { useCallback, useEffect, useState } from 'react';
import type { GameMapSnapshot, UIGameState } from '@sorrem-client/types';
import type { GameStateBridge } from '@sorrem-client/ui/shared/game-state-bridge';
import { useStoreSelector } from './use-store-selector';

/**
 * React hook that subscribes to a GameStateBridge but only re-renders when the
 * selected slice of state changes according to `equals` (defaults to Object.is).
 *
 * Subscribe through a selector to prevent re-renders caused by unrelated
 * state updates (e.g. playerMapPos changes from the game loop). Built on
 * `useSyncExternalStore`, so it is tearing-safe; a selector that returns a
 * new object each call needs an `equals` that compares its fields.
 */
export function useGameSelector<T>(
    bridge: GameStateBridge,
    selector: (state: UIGameState) => T,
    equals: (a: T, b: T) => boolean = Object.is,
): T {
    const subscribe = useCallback((onChange: () => void) => bridge.subscribe(onChange), [bridge]);
    const getState = useCallback(() => bridge.getState(), [bridge]);
    return useStoreSelector(subscribe, getState, selector, equals);
}

/** React hook for debug pages that need map payload from websocket events. */
export function useGameMap(bridge: GameStateBridge): GameMapSnapshot | null {
    const [map, setMap] = useState<GameMapSnapshot | null>(() => bridge.getMap());

    useEffect(() => {
        setMap(bridge.getMap());
        return bridge.subscribeMap(setMap);
    }, [bridge]);

    return map;
}
